import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { Loader2, Save } from "lucide-react";
import { Button } from "src/components/ui/button";
import { Card, CardContent } from "src/components/ui/card";
import { Input } from "src/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "src/components/ui/select";
import { generateId } from "src/lib/utils";
import { useLanguage } from "src/context/LanguageContext";
import { toast } from "sonner";
import QuizBuilder from "../../../../components/Quiz/quiz-builder";
import { BaseUrlApi } from "../../../../lib/api";
import EditorTextArea from "../../../../components/EditorTextArea";
import { QuestionType } from "../../../../lib/types";
import { SubLessonData } from "../../../../lib/languages";

export default function EditSurvey() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = SubLessonData[language] || SubLessonData["en"];

  const location = useLocation();
  const survey = location?.state?.survey;

  const [loading, setLoading] = useState(false);
  const [fetchingSurvey, setFetchingSurvey] = useState(true);
  const [errors, setErrors] = useState({});

  // Initialize form state for surveys
  const [form, setForm] = useState({
    title: "",
    description: "",
    questions: [
      {
        id: generateId(),
        type: QuestionType.SINGLE_CHOICE,
        text: "",
        options: [
          { id: generateId(), text: "" },
          { id: generateId(), text: "" },
        ],
      },
    ],
  });

  // Fetch survey data
  useEffect(() => {
    const fetchSurvey = async () => {
      try {
        setFetchingSurvey(true);
        const response = await axios.get(`${BaseUrlApi}/surveys/${survey?.id}`);
        const surveyData = response.data.data;

        const formattedQuestions = surveyData.questions.map((q) => ({
          id: q.id || generateId(),
          type: q.type || QuestionType.SINGLE_CHOICE,
          text: q.text || "",
          options: q.options.map((o) => ({
            id: o.id || generateId(),
            text: o.text || "",
          })),
        }));

        setForm({
          title: surveyData.title || "",
          description: surveyData.description || "",
          questions: formattedQuestions,
        });
      } catch (error) {
        console.error("Error fetching survey:", error);
        toast("Error", {
          description: "Failed to fetch survey data. Please try again.",
        });
      } finally {
        setFetchingSurvey(false);
      }
    };

    fetchSurvey();
  }, []);

  // Update questions for survey
  const updateQuestions = useCallback((questions) => {
    const formattedQuestions = questions.map((question) => ({
      text: question.text,
      type: question.type,
      options: question.options.map((option) => ({
        text: option.text,
      })),
    }));
    setForm((prevForm) => ({ ...prevForm, questions: formattedQuestions }));
  }, []);

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    if (!form.title) newErrors.title = ["Title is required"];
    if (form.questions.length === 0)
      newErrors.questions = ["At least one question is required"];
    else {
      const invalidQuestions = form.questions.filter(
        (q) =>
          !q.text ||
          ([QuestionType.SINGLE_CHOICE, QuestionType.MULTIPLE_CHOICE].includes(
            q.type
          ) &&
            q.options.length < 2)
      );
      if (invalidQuestions.length > 0)
        newErrors.questions = ["Some questions are incomplete"];
    }
    return newErrors;
  };

  // Handle survey submission
  const handleSubmitSurvey = async () => {
    setLoading(true);
    setErrors({});
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setLoading(false);
      toast("Validation Error", {
        description: "Please check the form for errors",
      });
      return;
    }

    try {
      const data = {
        title: form.title,
        description: form.description,
        questions: form.questions.map((question, i) => ({
          text: question.text,
          type: question.type,
          options: question.options.map((option, j) => option.text),
        })),
      };

      await axios.put(`${BaseUrlApi}/surveys/${1}`, data);

      toast("Success", { description: "Survey updated successfully" });
      navigate(-1);
    } catch (error) {
      console.error("Error updating survey:", error);
      toast("Error", {
        description: "Failed to update survey. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-10" dir={language === "ar" ? "rtl" : "ltr"}>
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="w-full sm:w-fit">
          <p className="font-bold text-2xl pt-5" id="category">
            {language === "ar" ? "تعديل الاستطلاع" : "Edit Survey"}
          </p>
          <p className="font-medium text-sm text-muted-foreground">
            {language === "ar"
              ? "تعديل استطلاع موجود"
              : "Edit an existing survey"}
          </p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-fit">
          <Button
            className="w-full sm:w-fit h-10 bg-blue-600 text-white hover:bg-blue-600/80 rounded-xl"
            onClick={handleSubmitSurvey}
            disabled={loading || fetchingSurvey}
          >
            {loading ? (
              <div className="animate-spin mr-2">
                <Loader2 className="h-4 w-4" />
              </div>
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {language === "ar" ? "حفظ الاستطلاع" : "Save Survey"}
          </Button>
        </div>
      </div>

      {fetchingSurvey ? (
        <div className="flex items-center justify-center p-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <span className="ml-2 text-lg">
            {language === "ar"
              ? "جاري تحميل الاستطلاع..."
              : "Loading Survey..."}
          </span>
        </div>
      ) : (
        <>
          <div className="col-span-1 lg:col-span-2">
            <Card className="h-full flex flex-col">
              <CardContent className="p-4 space-y-4 flex-1 flex flex-col">
                <p className="font-bold text-xl border-b pb-2">
                  {t.generalInformation}
                </p>
                <div className="space-y-4 flex-1 flex flex-col">
                  <div className="flex items-center gap-4 flex-col sm:flex-row">
                    <div className="flex flex-col gap-2 w-full">
                      <label htmlFor="title" className="text-sm">
                        {language === "ar" ? "العنوان" : "Title"}
                      </label>
                      <Input
                        readOnly={loading}
                        id="title"
                        name="title"
                        type="text"
                        placeholder={
                          language === "ar" ? "أدخل العنوان" : "Enter title "
                        }
                        value={form.title}
                        onChange={(e) =>
                          setForm((prevForm) => ({
                            ...prevForm,
                            title: e.target.value,
                          }))
                        }
                      />
                      {errors?.title && (
                        <p className="text-red-500 font-semibold text-sm">
                          {errors.title[0]}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 flex-1">
                    <label htmlFor="description" className="text-sm">
                      {language === "ar" ? "الوصف" : "Description"}
                    </label>
                    <EditorTextArea
                      form={form}
                      setForm={setForm}
                      disabled={loading}
                      en="description"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="col-span-1 lg:col-span-2">
            <Card className="h-full flex flex-col">
              <CardContent className="p-4">
                <p className="font-bold text-xl border-b pb-2 mb-4">
                  {language === "ar" ? "أسئلة الاستطلاع" : "Survey Questions"}
                </p>
                <QuizBuilder
                  initialQuestions={form.questions.map((q) => ({
                    id: q.id,
                    type: q.type,
                    text: q.text || "",
                    options: q.options.map((o) => ({
                      id: o.id,
                      text: o.text || "",
                    })),
                  }))}
                  onQuestionsChange={updateQuestions}
                  disabled={loading}
                  isSurvey={true}
                />
                {errors?.questions && (
                  <p className="text-red-500 font-semibold text-sm mt-2">
                    {errors.questions[0]}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
